import React, { useState, useEffect } from 'react';
import {
  CreditCard,
  Shield,
  Zap,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Save,
  Loader2,
  Key,
  Building2
} from 'lucide-react';
import lawpayService from '../../services/lawpayService';

const LawPaySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [configStatus, setConfigStatus] = useState(null);
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [toggling, setToggling] = useState(false);

  const [formData, setFormData] = useState({
    api_key: '',
    api_secret: '',
    public_key: '',
    environment: 'sandbox',
    trust_card_account_id: '',
    trust_echeck_account_id: '',
    trust_paylater_account_id: '',
    operating_card_account_id: '',
    operating_echeck_account_id: '',
    operating_paylater_account_id: '',
    webhook_secret: '',
    default_payment_methods: ['credit_card'],
    surcharge_enabled: false
  });

  const loadStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await lawpayService.getStatus();
      setConfigStatus(response.data);

      // If configured, try to load full config
      if (response.data.configured) {
        try {
          const configResponse = await lawpayService.getConfig();
          const config = configResponse.data;
          setFormData({
            api_key: '', // Don't show existing key
            api_secret: '', // Don't show existing secret
            public_key: config.public_key || '',
            environment: config.environment || 'sandbox',
            trust_card_account_id: config.trust_card_account_id || '',
            trust_echeck_account_id: config.trust_echeck_account_id || '',
            trust_paylater_account_id: config.trust_paylater_account_id || '',
            operating_card_account_id: config.operating_card_account_id || '',
            operating_echeck_account_id: config.operating_echeck_account_id || '',
            operating_paylater_account_id: config.operating_paylater_account_id || '',
            webhook_secret: '', // Don't show existing secret
            default_payment_methods: config.default_payment_methods || ['credit_card'],
            surcharge_enabled: config.surcharge_enabled || false
          });
        } catch (configErr) {
          // User may not have permission to view full config
          console.log('Could not load full config:', configErr);
        }
      }
    } catch (err) {
      setError('Failed to load LawPay configuration status');
      console.error('Error loading LawPay status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setSuccess('');
  };

  const handlePaymentMethodChange = (method, checked) => {
    setFormData(prev => {
      const methods = new Set(prev.default_payment_methods);
      if (checked) {
        methods.add(method);
      } else {
        methods.delete(method);
      }
      return { ...prev, default_payment_methods: [...methods] };
    });
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Build the config data
      const configData = {
        environment: formData.environment,
        default_payment_methods: formData.default_payment_methods
      };

      // Only include credentials if provided (required for new, optional for update)
      if (formData.api_key.trim()) {
        configData.api_key = formData.api_key.trim();
      }
      if (formData.api_secret.trim()) {
        configData.api_secret = formData.api_secret.trim();
      }

      // Per-payment-method account IDs
      const accountFields = [
        'trust_card_account_id', 'trust_echeck_account_id', 'trust_paylater_account_id',
        'operating_card_account_id', 'operating_echeck_account_id', 'operating_paylater_account_id'
      ];
      for (const field of accountFields) {
        if (formData[field].trim()) {
          configData[field] = formData[field].trim();
        }
      }
      if (formData.webhook_secret.trim()) {
        configData.webhook_secret = formData.webhook_secret.trim();
      }
      if (formData.public_key.trim()) {
        configData.public_key = formData.public_key.trim();
      }

      configData.surcharge_enabled = formData.surcharge_enabled;

      if (configStatus?.configured) {
        await lawpayService.updateConfig(configData);
      } else {
        await lawpayService.configure(configData);
      }
      setSuccess('LawPay configuration saved successfully');

      // Reload status to show updated state
      await loadStatus();
    } catch (err) {
      console.error('Error saving LawPay config:', err);
      setError(err.response?.data?.detail || 'Failed to save LawPay configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    const newState = !configStatus?.is_active;
    const action = newState ? 'enable' : 'disable';
    if (!window.confirm(`Are you sure you want to ${action} the LawPay integration?`)) return;
    setToggling(true);
    setError('');
    setSuccess('');
    try {
      await lawpayService.updateConfig({ is_active: newState });
      setSuccess(`LawPay integration ${newState ? 'enabled' : 'disabled'} successfully`);
      await loadStatus();
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${action} LawPay integration`);
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-yellow-500" />
        <span className="ml-2 text-gray-600">Loading LawPay settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-2">
          <CreditCard className="w-6 h-6 text-yellow-500" />
          <h2 className="text-xl font-semibold text-gray-900">LawPay Payment Integration</h2>
        </div>
        <p className="text-gray-600">
          Connect your LawPay account to accept online payments from clients. LawPay is designed specifically for law firms with trust account compliance.
        </p>
      </div>

      {/* Status Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-gray-500" />
          Connection Status
        </h3>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {configStatus?.configured ? (
              <>
                <div className="flex items-center gap-2">
                  {configStatus.is_active ? (
                    <><CheckCircle className="w-5 h-5 text-green-500" /><span className="text-green-700 font-medium">Connected</span></>
                  ) : (
                    <><AlertCircle className="w-5 h-5 text-yellow-500" /><span className="text-yellow-700 font-medium">Disabled</span></>
                  )}
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  configStatus.environment === 'production'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {configStatus.environment === 'production' ? 'Production' : 'Sandbox'}
                </span>
                {configStatus.account_status && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    configStatus.account_status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {configStatus.account_status}
                  </span>
                )}
              </>
            ) : (
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-gray-400" />
                <span className="text-gray-600">Not configured</span>
              </div>
            )}
          </div>
          {configStatus?.configured && (
            <button
              onClick={handleToggleActive}
              disabled={toggling}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 ${
                configStatus.is_active ? 'bg-green-500' : 'bg-gray-300'
              } ${toggling ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              title={configStatus.is_active ? 'Disable LawPay integration' : 'Enable LawPay integration'}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                configStatus.is_active ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-green-700">{success}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Credentials Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Key className="w-5 h-5 text-gray-500" />
            API Credentials
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            You can find your API credentials in your LawPay dashboard under Settings &gt; API Keys.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                API Key {!configStatus?.configured && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={formData.api_key}
                onChange={(e) => handleChange('api_key', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder={configStatus?.configured ? '••••••••••••••••' : 'Your API key...'}
                required={!configStatus?.configured}
              />
              <p className="mt-1 text-xs text-gray-500">
                {configStatus?.configured
                  ? 'Leave blank to keep existing key, or enter a new value to update'
                  : 'Your LawPay API key for authenticating requests'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Secret Key {!configStatus?.configured && <span className="text-red-500">*</span>}
              </label>
              <div className="relative">
                <input
                  type={showApiSecret ? 'text' : 'password'}
                  value={formData.api_secret}
                  onChange={(e) => handleChange('api_secret', e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder={configStatus?.configured ? '••••••••••••••••' : 'Your secret key...'}
                  required={!configStatus?.configured}
                />
                <button
                  type="button"
                  onClick={() => setShowApiSecret(!showApiSecret)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                >
                  {showApiSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {configStatus?.configured
                  ? 'Leave blank to keep existing secret, or enter a new value to update'
                  : 'Your LawPay secret key for server-side API calls'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Webhook Secret
              </label>
              <div className="relative">
                <input
                  type={showWebhookSecret ? 'text' : 'password'}
                  value={formData.webhook_secret}
                  onChange={(e) => handleChange('webhook_secret', e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder={configStatus?.configured ? '••••••••••••••••' : 'whsec_...'}
                />
                <button
                  type="button"
                  onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                  className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                >
                  {showWebhookSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Optional. Required for receiving payment notifications via webhooks.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Public Key
              </label>
              <input
                type="text"
                value={formData.public_key}
                onChange={(e) => handleChange('public_key', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder={configStatus?.configured ? (formData.public_key || 'Not set') : 'm_...'}
              />
              <p className="mt-1 text-xs text-gray-500">
                Your AffiniPay public key for hosted payment fields. Found in your LawPay dashboard under Settings &gt; API Keys.
              </p>
            </div>
          </div>
        </div>

        {/* Environment Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-gray-500" />
            Environment
          </h3>

          <div className="space-y-3">
            <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="environment"
                value="sandbox"
                checked={formData.environment === 'sandbox'}
                onChange={(e) => handleChange('environment', e.target.value)}
                className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300"
              />
              <div className="ml-3">
                <span className="text-sm font-medium text-gray-900">Sandbox</span>
                <p className="text-xs text-gray-500">Test environment for development and testing. No real transactions.</p>
              </div>
            </label>

            <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="environment"
                value="production"
                checked={formData.environment === 'production'}
                onChange={(e) => handleChange('environment', e.target.value)}
                className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300"
              />
              <div className="ml-3">
                <span className="text-sm font-medium text-gray-900">Production</span>
                <p className="text-xs text-gray-500">Live environment for processing real payments.</p>
              </div>
            </label>
          </div>
        </div>

        {/* Account IDs Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-gray-500" />
            Account Configuration
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Enter the account IDs from your LawPay dashboard for each payment method type.
            Each account type (Trust / Operating) has separate IDs for Card, eCheck, and Pay Later.
          </p>

          {/* Trust Accounts */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wide">Trust Accounts</h4>
            <p className="text-xs text-gray-500 mb-3">For client trust deposits (IOLTA compliant)</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Card</label>
                <input
                  type="text"
                  value={formData.trust_card_account_id}
                  onChange={(e) => handleChange('trust_card_account_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Trust Card account ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">eCheck</label>
                <input
                  type="text"
                  value={formData.trust_echeck_account_id}
                  onChange={(e) => handleChange('trust_echeck_account_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Trust eCheck account ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pay Later</label>
                <input
                  type="text"
                  value={formData.trust_paylater_account_id}
                  onChange={(e) => handleChange('trust_paylater_account_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Trust Pay Later account ID"
                />
              </div>
            </div>
          </div>

          {/* Operating Accounts */}
          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wide">Operating Accounts</h4>
            <p className="text-xs text-gray-500 mb-3">For earned fees and operating expenses</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Card</label>
                <input
                  type="text"
                  value={formData.operating_card_account_id}
                  onChange={(e) => handleChange('operating_card_account_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Operating Card account ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">eCheck</label>
                <input
                  type="text"
                  value={formData.operating_echeck_account_id}
                  onChange={(e) => handleChange('operating_echeck_account_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Operating eCheck account ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pay Later</label>
                <input
                  type="text"
                  value={formData.operating_paylater_account_id}
                  onChange={(e) => handleChange('operating_paylater_account_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="Operating Pay Later account ID"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Payment Methods Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-gray-500" />
            Payment Methods
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Select which payment methods to accept from clients.
          </p>

          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.default_payment_methods.includes('credit_card')}
                onChange={(e) => handlePaymentMethodChange('credit_card', e.target.checked)}
                className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Credit Card</span>
              <span className="ml-2 text-xs text-gray-500">(Visa, Mastercard, American Express, Discover)</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.default_payment_methods.includes('echeck')}
                onChange={(e) => handlePaymentMethodChange('echeck', e.target.checked)}
                className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">eCheck / ACH</span>
              <span className="ml-2 text-xs text-gray-500">(Bank account transfers)</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.default_payment_methods.includes('pay_later')}
                onChange={(e) => handlePaymentMethodChange('pay_later', e.target.checked)}
                className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Pay Later</span>
              <span className="ml-2 text-xs text-gray-500">(LawPay Pay Later financing)</span>
            </label>
          </div>
        </div>

        {/* Credit Card Surcharge Section */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-gray-500" />
            Credit Card Surcharge
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Pass credit card processing fees to clients as a surcharge on payments. The surcharge rate is configured in your LawPay dashboard.
          </p>

          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.surcharge_enabled}
                onChange={(e) => handleChange('surcharge_enabled', e.target.checked)}
                className="h-4 w-4 mt-1 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
              />
              <div>
                <span className="text-sm font-medium text-gray-700">Enable credit card surcharging</span>
                <p className="text-xs text-gray-500 mt-1">
                  When enabled, clients paying by credit card will see a surcharge added to their payment. Debit and prepaid cards are automatically excluded (federal law).
                </p>
              </div>
            </label>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-yellow-800">
                  <p className="font-medium mb-1">Compliance Notice</p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Surcharges are prohibited in CT, MA, ME, and CA. CO caps surcharges at 2%.</li>
                    <li>Visa caps surcharges at 3% or actual processing cost (whichever is lower).</li>
                    <li>The surcharge rate must be configured in your LawPay dashboard, not here.</li>
                    <li>Ensure your firm complies with state and card network regulations before enabling.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Configuration
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LawPaySettings;
